import React from 'react';
import type { Vendor } from '../../services/vendorAPI';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Edit,
  ExternalLink
} from 'lucide-react';

interface VendorListProps {
  vendors: Vendor[];
  onVendorClick: (vendor: Vendor) => void;
  onEdit: (vendor: Vendor) => void;
  isLoading: boolean;
}

const VendorList: React.FC<VendorListProps> = ({
  vendors,
  onVendorClick,
  onEdit,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-20 bg-gray-200 rounded-lg" />
        ))}
      </div>
    );
  }

  const getSoc2Badge = (status: string) => {
    switch (status) {
      case 'current':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            SOC 2 Current
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
            <XCircle className="w-3 h-3" />
            SOC 2 Expired
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
            <Clock className="w-3 h-3" />
            SOC 2 Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
            N/A
          </span>
        );
    }
  };

  const getDpaBadge = (status: string) => {
    switch (status) {
      case 'signed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            DPA Signed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
            <AlertTriangle className="w-3 h-3" />
            DPA Pending
          </span>
        );
      case 'declined':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
            <XCircle className="w-3 h-3" />
            DPA Declined
          </span>
        );
      default:
        return null;
    }
  };

  const getRiskBadge = (level: string, score: number) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200'
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${colors[level as keyof typeof colors] || colors.medium}`}>
        <Shield className="w-3 h-3" />
        {score}
      </span>
    );
  };

  const getReviewBadge = (status: string) => {
    switch (status) {
      case 'current':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
            <CheckCircle2 className="w-3 h-3" />
            Review Current
          </span>
        );
      case 'overdue':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-full">
            <AlertTriangle className="w-3 h-3" />
            Review Overdue
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-700 rounded-full">
            <Clock className="w-3 h-3" />
            Review Pending
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3">
      {vendors.map(vendor => (
        <div
          key={vendor.id}
          className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
          onClick={() => onVendorClick(vendor)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-medium text-gray-900">{vendor.vendor_name}</h3>
                {vendor.trust_center_url && (
                  <a
                    href={vendor.trust_center_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-blue-600 hover:text-blue-800"
                    title="View Trust Center"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                {vendor.service_description}
              </p>
              <div className="flex flex-wrap items-center gap-2">
                {getSoc2Badge(vendor.soc2_status)}
                {getDpaBadge(vendor.dpa_status)}
                {getReviewBadge(vendor.review_status)}
                {getRiskBadge(vendor.risk_level, vendor.risk_score)}
              </div>
            </div>
            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(vendor);
                }}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Edit vendor"
              >
                <Edit className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-500">
            <span className="capitalize">{vendor.service_category.replace(/_/g, ' ')}</span>
            <span>|</span>
            <span className="capitalize">{vendor.data_access_level.replace(/_/g, ' ')}</span>
            {vendor.next_review_date && (
              <>
                <span>|</span>
                <span>Next review: {new Date(vendor.next_review_date).toLocaleDateString()}</span>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default VendorList;
